import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, ChevronLeft, ChevronRight, Loader2, Download, CalendarClock } from "lucide-react";
import { Layout } from "../components/Layout";
import { EmptyState } from "../components/EmptyState";
import { api } from "../lib/api";
import { buildProfileContext } from "../lib/profile";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { CAL_COLORS, NOVA_DEMO } from "../lib/demoData";
import { downloadICS } from "../lib/ics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const TYPES = ["publication", "promotion", "animation", "evenement", "campagne", "tache"];

export default function CalendarPage() {
  const nav = useNavigate();
  const { lang, t } = useLang();
  const { data, addCalendarEvent, removeCalendarEvent, launchPlan } = useApp();
  const WEEKDAYS = { fr: ["L", "M", "M", "J", "V", "S", "D"], en: ["M", "T", "W", "T", "F", "S", "S"], es: ["L", "M", "M", "J", "V", "S", "D"] }[lang] || ["L", "M", "M", "J", "V", "S", "D"];
  const [view, setView] = useState(typeof window !== "undefined" && window.innerWidth < 1024 ? "week" : "month");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "publication", channel: "", day: 5 });
  const [loading, setLoading] = useState(false);
  const events = data?.calendar || [];

  const submit = () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    addCalendarEvent({ ...form, day: Number(form.day) });
    toast.success("Action ajoutée au calendrier");
    setOpen(false); setForm({ title: "", type: "publication", channel: "", day: 5 });
  };

  // Real AI planning: dates come from the model logic, launched as tracked missions (no fabricated days).
  const autoPlan = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const message = "Génère un planning marketing concret pour les 30 prochains jours : publications, promotions, animations et tâches, réparties intelligemment dans le temps avec une échéance précise pour chacune.";
      const { data: plan } = await api.post("/ai/plan", { message, profile_context: buildProfileContext(data) });
      const { duplicate } = launchPlan({ ...plan, key: "planning mensuel calendrier" });
      if (duplicate) toast("Planning déjà actif", { description: "Ce planning a déjà été généré.", action: { label: "Voir le tableau de bord", onClick: () => nav("/app/dashboard") } });
      else toast.success("Planning mensuel généré par l'IA !", { description: "Ajouté au calendrier et au tableau de bord." });
    } catch { toast.error("Impossible de générer le planning. Réessayez."); }
    finally { setLoading(false); }
  };

  const days = Array.from({ length: 30 }, (_, i) => i + 1);

  const exportICS = () => {
    if (!events.length) { toast.error("Aucune action à exporter."); return; }
    const n = downloadICS(events);
    toast.success(`${n} actions exportées (.ics) avec rappels`);
  };

  return (
    <Layout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div><h1 className="font-display font-black text-2xl lg:text-3xl">{t("title_calendar")}</h1><p className="text-sm text-[hsl(var(--muted-foreground))]">Votre planning commercial intelligent.</p></div>
        <div className="flex items-center gap-2">
          <Button data-testid="cal-export" onClick={exportICS} variant="outline" className="rounded-full border-white/10 bg-white/5 text-xs h-11 sm:h-9"><Download size={14} className="mr-1" /> Exporter</Button>
          <Button data-testid="cal-autoplan" onClick={autoPlan} disabled={loading} variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-neon text-xs h-11 sm:h-9">
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />} Planning IA
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="cal-add" className="rounded-full bg-gradient-to-r from-primary to-neon text-white lb-glow-btn h-11 sm:h-9 text-xs"><Plus size={14} className="mr-1" /> Ajouter</Button></DialogTrigger>
            <DialogContent className="glass border-white/10">
              <DialogHeader><DialogTitle>Nouvelle action</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Titre</Label><Input data-testid="cal-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="cal-form-type" className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Jour</Label><Input data-testid="cal-form-day" type="number" min="1" max="30" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
                </div>
                <div><Label className="text-xs">Canal</Label><Input data-testid="cal-form-channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} placeholder="Instagram" className="mt-1.5 bg-white/5 border-white/10" /></div>
                <Button data-testid="cal-form-submit" onClick={submit} className="w-full rounded-full bg-primary text-white">Ajouter</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList className="bg-white/5"><TabsTrigger value="month" data-testid="cal-view-month" className="min-h-[44px] sm:min-h-0">Mois</TabsTrigger><TabsTrigger value="week" data-testid="cal-view-week" className="min-h-[44px] sm:min-h-0">Semaine</TabsTrigger></TabsList>
      </Tabs>

      {view === "month" ? (
        <div className="lb-card p-4">
          <div className="grid grid-cols-7 gap-1.5" data-testid="cal-month-grid">
            {WEEKDAYS.map((d, i) => <div key={i} className="text-center text-xs text-[hsl(var(--muted-foreground))] py-1">{d}</div>)}
            {days.map((day) => {
              const dayEvents = events.filter((e) => e.day === day);
              return (
                <div key={day} className="min-h-[54px] sm:min-h-[72px] rounded-lg bg-white/[0.02] border border-white/5 p-1 sm:p-1.5 hover:border-primary/30 transition-colors" data-testid={`cal-day-${day}`}>
                  <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{day}</span>
                  <div className="space-y-1 mt-0.5">
                    {dayEvents.slice(0, 2).map((e) => (
                      <div key={e.id} className="group flex items-center gap-1 text-[9px] px-1 py-0.5 rounded truncate" style={{ background: `${CAL_COLORS[e.type]}22`, color: CAL_COLORS[e.type] }}>
                        <span className="truncate flex-1">{e.title}</span>
                      </div>
                    ))}
                    {dayEvents.length > 2 && <span className="text-[9px] text-[hsl(var(--muted-foreground))]">+{dayEvents.length - 2}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {events.filter((e) => e.day <= 7 || e.missionId).sort((a, b) => a.day - b.day).map((e) => (
            <motion.div key={e.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="lb-card p-4 flex items-center gap-3" data-testid={`cal-week-${e.id}`}>
              <div className="w-2 h-10 rounded-full" style={{ background: CAL_COLORS[e.type] }} />
              <div className="flex-1"><p className="font-medium text-sm">{e.title}</p><p className="text-xs text-[hsl(var(--muted-foreground))] capitalize">Jour {e.day} · {e.type} · {e.channel}</p></div>
              <button data-testid={`cal-del-${e.id}`} onClick={() => { removeCalendarEvent(e.id); toast.success("Supprimé"); }} className="text-[hsl(var(--muted-foreground))] hover:text-red-400"><Trash2 size={16} /></button>
            </motion.div>
          ))}
          {events.filter((e) => e.day <= 7 || e.missionId).length === 0 && (
            <EmptyState testid="cal-week-empty" icon={CalendarClock}
              title={t("cal_empty_title")} hint={t("cal_empty_hint")}
              actionLabel={t("cal_empty_cta")} onAction={() => setOpen(true)} />
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4">
        {TYPES.map((t) => <div key={t} className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] capitalize"><span className="w-2.5 h-2.5 rounded-full" style={{ background: CAL_COLORS[t] }} />{t}</div>)}
      </div>
    </Layout>
  );
}
